// Apple Wallet Pass Generator
// Note: This creates a basic pass structure. For production, you'll need proper signing certificates.

export const generateAppleWalletPass = async (orderData) => {
    try {
        // Create the pass.json structure according to Apple's PassKit specification
        const passData = {
            formatVersion: 1,
            passTypeIdentifier: "pass.com.rotb.event.ticket", // This should match your Apple Developer certificate
            serialNumber: orderData.orderId,
            teamIdentifier: "ROTB123456", // Your Apple Developer Team ID
            organizationName: "Return of the Basement",
            description: "Phase 6 Inception Event Ticket",
            logoText: "ROTB",

            // Colors (RGB values)
            foregroundColor: "rgb(0, 255, 0)",
            backgroundColor: "rgb(0, 0, 0)",
            labelColor: "rgb(0, 255, 0)",

            // Event ticket specific fields
            eventTicket: {
                primaryFields: [
                    {
                        key: "event-name",
                        label: "EVENT",
                        value: orderData.eventName || "Phase 6 Inception",
                        textAlignment: "PKTextAlignmentCenter"
                    }
                ],
                secondaryFields: [
                    {
                        key: "date",
                        label: "DATE",
                        value: orderData.eventDate || "February .., 2026",
                        textAlignment: "PKTextAlignmentLeft"
                    },
                    {
                        key: "time",
                        label: "TIME",
                        value: orderData.eventTime || "22:00 - 06:00",
                        textAlignment: "PKTextAlignmentRight"
                    }
                ],
                auxiliaryFields: [
                    {
                        key: "location",
                        label: "LOCATION",
                        value: orderData.eventLocation || "Stormkop",
                        textAlignment: "PKTextAlignmentLeft"
                    },
                    {
                        key: "quantity",
                        label: "QTY",
                        value: orderData.quantity?.toString() || "1",
                        textAlignment: "PKTextAlignmentRight"
                    }
                ],
                backFields: [
                    {
                        key: "ticket-holder",
                        label: "TICKET HOLDER",
                        value: `${orderData.firstName || ''} ${orderData.lastName || ''}`.trim()
                    },
                    {
                        key: "order-id",
                        label: "ORDER ID",
                        value: orderData.orderId
                    },
                    {
                        key: "email",
                        label: "EMAIL",
                        value: orderData.email
                    },
                    {
                        key: "phone",
                        label: "PHONE",
                        value: orderData.phone || "N/A"
                    },
                    {
                        key: "total-amount",
                        label: "TOTAL PAID",
                        value: `€${orderData.totalAmount || '19.00'}`
                    },
                    {
                        key: "terms",
                        label: "TERMS & CONDITIONS",
                        value: "This ticket is non-refundable and non-transferable. Must be 18+ to attend. Valid ID required at entrance."
                    },
                    {
                        key: "contact",
                        label: "CONTACT",
                        value: "For questions: events@rotb.com"
                    }
                ]
            },

            // QR Code barcode
            barcode: {
                message: orderData.orderId,
                format: "PKBarcodeFormatQR",
                messageEncoding: "iso-8859-1",
                altText: `Order ID: ${orderData.orderId}`
            },

            // Location-based relevance (optional)
            locations: [
                {
                    latitude: 51.235344, // Approximate coordinates for Stormkop
                    longitude: 4.402771,
                    relevantText: "You're near the Phase 6 Inception venue!"
                }
            ],

            // Date relevance
            relevantDate: new Date("2026-02-15T22:00:00+01:00").toISOString(), // Event date

            // Expiration date (day after event)
            expirationDate: new Date("2026-02-16T06:00:00+01:00").toISOString(),

            // Voided if needed
            voided: false
        };

        // For now, create a downloadable JSON file
        // In production, this would be a proper signed .pkpass file
        const blob = new Blob([JSON.stringify(passData, null, 2)], {
            type: 'application/json'
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ROTB-${orderData.orderId}.json`; // In production: .pkpass

        // Style the download link to be invisible
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return {
            success: true,
            message: 'Pass data generated successfully. Note: For production, this needs to be properly signed by Apple.'
        };

    } catch (error) {
        console.error('Error generating Apple Wallet pass:', error);
        return {
            success: false,
            message: 'Failed to generate wallet pass',
            error: error.message
        };
    }
};

// Helper function to create a proper .pkpass file (requires backend implementation)
export const generateSignedPass = async (orderData) => {
    try {
        console.log('Generating Apple Wallet pass for order:', orderData.orderId);

        const response = await fetch('http://localhost:3001/api/generate-wallet-pass', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orderData })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to generate wallet pass');
        }

        // Get the JSON data
        const passData = await response.json();

        // Create a blob with the pass data
        const blob = new Blob([JSON.stringify(passData, null, 2)], {
            type: 'application/json'
        });

        // Download the file
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ROTB-${orderData.orderId}.json`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return {
            success: true,
            message: 'Apple Wallet pass structure downloaded! Note: For production, this needs to be properly signed by Apple.'
        };

    } catch (error) {
        console.error('Error generating signed pass:', error);

        // Fallback to basic JSON generation
        try {
            return await generateAppleWalletPass(orderData);
        } catch (fallbackError) {
            return {
                success: false,
                message: `Failed to generate wallet pass: ${error.message}`,
                error: error.message
            };
        }
    }
};
